/**
 * Hook for handling MasterCard 3DS authentication
 */
import { useCallback, useRef, useState } from 'react';
import type { Payment, PaymentOptions } from '../../../core/resources/payments';
import type { PaymentsResource } from '../../../core/resources/payments';
import type { UsePaymentOptions } from '../usePaymentQuery';
import { useThreedsModal } from '../useThreedsModal';

interface UseMasterCardActionParams {
    paymentsResource: PaymentsResource;
    startPolling: any;
    setError: (error: string | null) => void;
    setIsLoading: (loading: boolean) => void;
    hookOptionsRef: React.MutableRefObject<UsePaymentOptions | undefined>;
}

interface ThreeDSData {
    sessionId: string;
    merchantId: string;
    orderId: string;
    amount: number;
    currency: string;
    returnUrl: string;
    transactionId: string;
    scriptUrl: string;
    version?: string;
    authenticationStatus?: string;
    authenticationVersion?: string;
    gatewayRecommendation?: string;
    requiresChallenge?: boolean;
    sourceOfFunds?: any;
    correlationId?: string;
    dbSessionId?: string;
    paymentId?: string;
}

interface AuthenticationResult {
    success: boolean;
    authenticationStatus?: string;
    transactionId?: string;
    sessionId?: string;
    authenticationValue?: string;
    dsTransactionId?: string;
    acsTransactionId?: string;
    messageVersion?: string;
    eci?: string;
    correlationId?: string;
    gatewayRecommendation?: string;
    rawResult?: any;
    htmlRedirectCode?: string;
    restApiResponse?: any;
}

// Extend window object for MasterCard 3DS
declare global {
    interface Window {
        ThreeDS: {
            configure: (config: any) => void;
            isConfigured: () => boolean;
            initiateAuthentication: (
                orderId: string,
                transactionId: string,
                callback: (result: any) => void,
                optionalParams?: any,
            ) => void;
            authenticatePayer: (
                orderId: string,
                transactionId: string,
                callback: (result: any) => void,
                optionalParams?: any,
            ) => void;
        };
    }
}

export function useMasterCardAction({
    paymentsResource,
    startPolling,
    setError,
    setIsLoading,
    hookOptionsRef,
}: UseMasterCardActionParams) {
    const challengeInProgressRef = useRef(false);
    const [isShowingChallenge, setIsShowingChallenge] = useState(false);
    const { createThreedsModal, closeThreedsModal } = useThreedsModal();

    // Load MasterCard 3DS script
    const loadScript = useCallback(async (scriptUrl: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (window.ThreeDS) {
                resolve();
                return;
            }

            const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
            if (existingScript) {
                existingScript.addEventListener('load', () => resolve());
                existingScript.addEventListener('error', () => reject(new Error('Failed to load 3DS script')));
                return;
            }

            const script = document.createElement('script');
            script.src = scriptUrl;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load 3DS script'));
            document.head.appendChild(script);
        });
    }, []);

    const handleMasterCardAuth = useCallback(
        async (payment: Payment, actionData: any, options: PaymentOptions = {}, handlePaymentAction: any) => {
            if (challengeInProgressRef.current) {
                console.log('MasterCard 3DS challenge already in progress');
                return;
            }

            const threeDSData = actionData?.metadata?.threeds as ThreeDSData | undefined;

            if (!threeDSData?.sessionId || !threeDSData?.merchantId) {
                const errorMsg = 'Missing MasterCard 3DS authentication data';
                console.error(errorMsg);
                setError(errorMsg);
                setIsLoading(false);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({
                    code: 'MASTERCARD_MISSING_DATA',
                    message: errorMsg,
                    payment,
                });
                return;
            }

            try {
                challengeInProgressRef.current = true;
                setIsLoading(false);

                console.log('[MasterCard 3DS] Starting authentication:', {
                    sessionId: threeDSData.sessionId,
                    orderId: threeDSData.orderId,
                    transactionId: threeDSData.transactionId,
                });

                // Load the MasterCard 3DS script
                await loadScript(threeDSData.scriptUrl);

                // Create hidden container for 3DS iframe
                const containerId = 'mastercard-3ds-container';
                let container = document.getElementById(containerId);
                if (!container) {
                    container = document.createElement('div');
                    container.id = containerId;
                    container.style.cssText = 'position: absolute; top: -9999px; left: -9999px; width: 1px; height: 1px; overflow: hidden;';
                    document.body.appendChild(container);
                }

                // Configure ThreeDS API
                console.log('[MasterCard 3DS] Configuring ThreeDS API');
                await new Promise<void>((resolve, reject) => {
                    window.ThreeDS.configure({
                        merchantId: threeDSData.merchantId,
                        sessionId: threeDSData.sessionId,
                        containerId,
                        callback: function () {
                            if (window.ThreeDS.isConfigured()) {
                                console.log('[MasterCard 3DS] Configuration completed');
                                resolve();
                            } else {
                                reject(new Error('ThreeDS configuration failed'));
                            }
                        },
                        configuration: {
                            userLanguage: 'en-US',
                            wsVersion: threeDSData.version ? parseInt(threeDSData.version, 10) : 72,
                        },
                    });
                });

                // Authenticate payer
                const authResult = await new Promise<AuthenticationResult>((resolve) => {
                    window.ThreeDS.authenticatePayer(
                        threeDSData.orderId,
                        threeDSData.transactionId,
                        (payerResult: any) => {
                            console.log('[MasterCard 3DS] authenticatePayer result:', payerResult);
                            resolve(payerResult as AuthenticationResult);
                        },
                    );
                });

                // Check if challenge HTML is present
                const challengeHtml = authResult?.htmlRedirectCode || authResult?.restApiResponse?.authentication?.redirect?.html;

                if (challengeHtml) {
                    setIsShowingChallenge(true);

                    // Create modal for challenge
                    const modal = createThreedsModal({
                        containerId: 'mastercard-3ds-modal',
                        mode: 'auto-fit',
                        onClose: () => {
                            console.log('[MasterCard 3DS] Modal closed by user');
                            setIsShowingChallenge(false);
                            challengeInProgressRef.current = false;
                            const errorMsg = 'User closed the authentication modal';
                            setError(errorMsg);
                            options.onFailure?.(errorMsg);
                            options.onPaymentFailed?.({
                                code: 'MASTERCARD_CANCELLED',
                                message: errorMsg,
                                payment,
                            });
                        },
                    });

                    const contentElement = modal.getContentElement();
                    if (contentElement) {
                        contentElement.innerHTML = challengeHtml;

                        // Auto-submit form if present
                        setTimeout(() => {
                            const frictionlessForm = contentElement.querySelector('#threedsFrictionLessRedirectForm');
                            const challengeForm = contentElement.querySelector('#threedsChallengeRedirectForm');
                            const form = frictionlessForm ?? challengeForm;

                            if (form) {
                                console.log('[MasterCard 3DS] Auto-submitting form');
                                (form as HTMLFormElement).submit();

                                // For frictionless flow, close modal and complete payment
                                if (frictionlessForm) {
                                    setTimeout(async () => {
                                        closeThreedsModal('mastercard-3ds-modal');
                                        setIsShowingChallenge(false);
                                        challengeInProgressRef.current = false;

                                        // Complete payment after frictionless 3DS
                                        if (threeDSData.paymentId) {
                                            const resumedPayment = await paymentsResource.completePaymentAfterAction(threeDSData.paymentId);
                                            await handlePaymentAction(resumedPayment, options);
                                        }
                                    }, 1000);
                                } else {
                                    // Challenge flow - listen for completion
                                    const handleIframeMessage = async (event: MessageEvent) => {
                                        const messageData = event.data;
                                        let isComplete = false;

                                        if (typeof messageData === 'string') {
                                            isComplete = messageData.toLowerCase().includes('complete') ||
                                                messageData.toLowerCase().includes('success');
                                        } else if (typeof messageData === 'object' && messageData !== null) {
                                            isComplete = messageData.result === 'SUCCESS' ||
                                                messageData.transStatus === 'Y' ||
                                                messageData.type === '3DSAuthenticationComplete';
                                        }

                                        if (isComplete) {
                                            console.log('[MasterCard 3DS] Challenge completed');
                                            window.removeEventListener('message', handleIframeMessage);
                                            closeThreedsModal('mastercard-3ds-modal');
                                            setIsShowingChallenge(false);
                                            challengeInProgressRef.current = false;

                                            // Complete payment after challenge
                                            if (threeDSData.paymentId) {
                                                const resumedPayment = await paymentsResource.completePaymentAfterAction(threeDSData.paymentId);
                                                await handlePaymentAction(resumedPayment, options);
                                            }
                                        }
                                    };

                                    window.addEventListener('message', handleIframeMessage);

                                    // Timeout after 5 minutes
                                    setTimeout(() => {
                                        window.removeEventListener('message', handleIframeMessage);
                                        closeThreedsModal('mastercard-3ds-modal');
                                        setIsShowingChallenge(false);
                                        challengeInProgressRef.current = false;
                                        const errorMsg = '3DS authentication timed out';
                                        setError(errorMsg);
                                        options.onFailure?.(errorMsg);
                                    }, 300000);
                                }
                            }
                        }, 100);
                    }
                } else {
                    // No challenge required - frictionless flow
                    console.log('[MasterCard 3DS] No challenge required');
                    challengeInProgressRef.current = false;

                    if (threeDSData.paymentId) {
                        const resumedPayment = await paymentsResource.completePaymentAfterAction(threeDSData.paymentId);
                        await handlePaymentAction(resumedPayment, options);
                    }
                }

                // Cleanup container
                const cleanupContainer = document.getElementById('mastercard-3ds-container');
                if (cleanupContainer && document.body.contains(cleanupContainer)) {
                    document.body.removeChild(cleanupContainer);
                }

            } catch (error) {
                challengeInProgressRef.current = false;
                const errorMsg = error instanceof Error ? error.message : 'MasterCard 3DS authentication failed';
                console.error('[MasterCard 3DS] Error:', error);
                setError(errorMsg);
                setIsLoading(false);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({
                    code: 'MASTERCARD_AUTH_FAILED',
                    message: errorMsg,
                    payment,
                });

                if (hookOptionsRef.current?.onPaymentFailed) {
                    hookOptionsRef.current.onPaymentFailed(errorMsg, {
                        isRedirectReturn: false,
                    });
                }
            }
        },
        [paymentsResource, loadScript, createThreedsModal, closeThreedsModal, setError, setIsLoading, hookOptionsRef],
    );

    return { handleMasterCardAuth };
}
